'use client';

import { useGetImageModerationStatus } from '@morfi/backend-sdk-v2/client';
import { useEffect, useState } from 'react';

import { useUploadTrackingStore } from '@/stores/upload-tracking';

const MAX_POLL_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 1500;

export interface NsfwStatusByUrl {
  /** True once the moderator has classified this URL as flagged. */
  isFlagged: boolean;
  /** True only for a confirmed flagged or minors-takedown verdict. */
  isNsfw: boolean;
  /** True while still awaiting a verdict (or sha256 unknown). */
  isPending: boolean;
  /** Raw status, when known. */
  status: 'pending' | 'clean' | 'flagged' | 'minors_takedown' | 'failed' | undefined;
  /** True when this exact URL belongs to the current upload session. */
  isTracked: boolean;
}

/**
 * Resolve NSFW moderation status for a freshly-uploaded image URL using
 * the URL→sha256 map stashed by `<CropModal>` on upload. Polls
 * `/v1/images/{sha256}/moderation-status` every 1.5s until a verdict
 * lands or 12s elapses. After that the cron sweeper takes over server-
 * side and the viewer-side JOIN surfaces the verdict on next render.
 *
 * Returns `isPending=true` when:
 * - The URL isn't in the upload-tracking store (e.g. preset image, page
 *   refresh after upload), in which case there's nothing to poll.
 * - A verdict has not yet landed.
 */
export function useNsfwStatusByUrl(url: string | null | undefined): NsfwStatusByUrl {
  const sha256 = useUploadTrackingStore((s) =>
    url === null || url === undefined || url === '' ? undefined : s.urlToSha.get(url),
  );
  const knownStatus = useUploadTrackingStore((s) =>
    url === null || url === undefined || url === '' ? undefined : s.urlToStatus.get(url),
  );
  const [polling, setPolling] = useState<{ sha256: string; attempts: number } | null>(null);
  const attempts = polling !== null && polling.sha256 === sha256 ? polling.attempts : 0;
  const trackUpload = useUploadTrackingStore((s) => s.track);

  // ALLOW-UNSCOPED: v2 useGetImageModerationStatus returns 2 fields with no nested selection; the codegen-emitted Args type omits `fields`.
  const moderation = useGetImageModerationStatus(
    { sha256: sha256 ?? '' },
    { enabled: knownStatus === undefined && sha256 !== undefined && attempts < MAX_POLL_ATTEMPTS },
  );

  const status = knownStatus ?? moderation.data?.status;
  const error = moderation.error;
  // 404 (or any error after the first attempt) means the sha isn't tracked
  // server-side — usually because the user uploaded a new image, the URL→sha
  // map in sessionStorage is stale, or the row hasn't been written yet.
  // Don't burn 8 retries on a known-dead lookup.
  const giveUp = error !== undefined;

  const refetch = moderation.refetch;
  useEffect((): (() => void) | undefined => {
    if (knownStatus !== undefined) return undefined;
    if (sha256 === undefined) return undefined;
    if (status !== undefined && status !== 'pending') return undefined;
    if (attempts >= MAX_POLL_ATTEMPTS) return undefined;
    if (giveUp) return undefined;
    const t = setTimeout((): void => {
      refetch();
      setPolling({ sha256, attempts: attempts + 1 });
    }, POLL_INTERVAL_MS);
    return (): void => {
      clearTimeout(t);
    };
  }, [knownStatus, sha256, status, attempts, refetch, giveUp]);

  useEffect((): void => {
    const resolved = moderation.data?.status;
    if (
      url === null ||
      url === undefined ||
      url.length === 0 ||
      sha256 === undefined ||
      resolved === undefined ||
      resolved === 'pending'
    ) {
      return;
    }
    trackUpload(url, sha256, resolved);
  }, [moderation.data?.status, sha256, trackUpload, url]);

  const hasUrl = url !== null && url !== undefined && url !== '';
  const isFlagged = status === 'flagged' || status === 'minors_takedown';
  const isPending =
    hasUrl &&
    knownStatus === undefined &&
    !giveUp &&
    (sha256 === undefined ||
      status === undefined ||
      (status === 'pending' && attempts < MAX_POLL_ATTEMPTS));
  const isNsfw = isFlagged;

  return { isFlagged, isNsfw, isPending, status, isTracked: sha256 !== undefined };
}
